package link.erpango.quip.domain.noteimport

import kotlinx.datetime.LocalDate
import link.erpango.quip.domain.entities.DExtractedMaintenance
import link.erpango.quip.domain.entities.DExtractedSparePart
import link.erpango.quip.domain.entities.DExtractedVehicle
import link.erpango.quip.domain.entities.DExtractionResponse
import link.erpango.quip.domain.extraction.ExtractionConfidence

/**
 * Maps the backend LLM extraction into the review model.
 *
 * The backend already validates the model output, but the client does not trust it either:
 * the same bounds are applied again here (string lengths, catalog ids, VIN format, ranges,
 * vehicle_index), so a misbehaving model or backend can never put arbitrary values into a
 * form. Anything that does not pass is dropped, never shown.
 *
 * Ids are "av0", "am0", "ap0"... so they never clash with the local draft's ids.
 */
fun DExtractionResponse.toImportDraft(ctx: DraftContext): ImportDraft {
    val vehicleDrafts = vehicles.take(MAX_VEHICLES).mapIndexed { index, v -> v.toDraft("av$index", ctx) }
    fun refFor(index: Int): VehicleRef =
        vehicleDrafts.getOrNull(index)?.let { VehicleRef.Draft(it.id) } ?: VehicleRef.Unassigned

    return ImportDraft(
        vehicles = vehicleDrafts,
        maintenances = maintenances.take(MAX_ITEMS).mapIndexed { index, m ->
            m.toDraft("am$index", refFor(m.vehicleIndex), ctx)
        },
        spareParts = spareParts.take(MAX_ITEMS).mapIndexed { index, p ->
            p.toDraft("ap$index", refFor(p.vehicleIndex), ctx)
        },
        aiApplied = true,
    )
}

private const val MAX_VEHICLES = 10
private const val MAX_ITEMS = 30
private const val MAX_KM = 3_000_000
private const val MAX_COST = 100_000.0
private const val MAX_QUANTITY = 1000.0
private const val MIN_CYLINDERS = 1
private const val MAX_CYLINDERS = 16
private const val MIN_TIRE_PRESSURE = 0.5
private const val MAX_TIRE_PRESSURE = 10.0

/** Same caps as the backend's STRING_CAPS. */
private val STRING_CAPS = mapOf(
    "brand" to 40, "model" to 60, "plate" to 15, "engine_oil" to 60, "front_tire_size" to 20,
    "rear_tire_size" to 20, "recommended_fuel" to 30, "color" to 30, "owner" to 60,
    "front_bulb_type" to 40, "rear_bulb_type" to 40, "battery_specs" to 60, "coolant_type" to 40,
    "reference" to 40, "unit" to 12, "description" to 300, "workshop" to 80, "mechanic" to 80,
)

private fun confidenceOf(map: Map<String, String>, field: String): ExtractionConfidence =
    when (map[field]) {
        "high" -> ExtractionConfidence.HIGH
        "low" -> ExtractionConfidence.LOW
        else -> ExtractionConfidence.MEDIUM
    }

/** A trimmed, non-blank string within its length cap, or null. */
private fun String?.capped(field: String): String? {
    val value = this?.trim()?.takeIf { it.isNotEmpty() } ?: return null
    return value.takeIf { it.length <= STRING_CAPS.getValue(field) }
}

private fun <T> ai(value: T?, confidence: ExtractionConfidence): SuggestedField<T>? =
    value?.let { SuggestedField(it, confidence, FieldSource.AI) }

private fun DExtractedVehicle.toDraft(id: String, ctx: DraftContext): VehicleDraft {
    fun c(field: String) = confidenceOf(fieldsConfidence, field)
    val year = year?.takeIf { it in 1886..(ctx.today.year + 1) }
    val fuel = recommendedFuel.capped("recommended_fuel")?.let { fuelOptionFromText(it) }
    return VehicleDraft(
        id = id,
        vin = ai(vin?.let { normalizeVin(it) }?.takeIf { isWellFormedVin(it) }, c("vin")),
        plate = ai(plate.capped("plate")?.let { normalizePlate(it) }, c("plate")),
        brand = ai(brand.capped("brand")?.let { canonicalName(it, ctx.brands) ?: it }, c("brand")),
        model = ai(model.capped("model"), c("model")),
        year = ai(year, c("year")),
        color = ai(color.capped("color")?.let { colorIdFromText(it) }, c("color")),
        owner = ai(owner.capped("owner"), c("owner")),
        vehicleType = ai(vehicleType?.let { vehicleTypeFromCode(it) }, c("vehicle_type")),
        engineOil = ai(engineOil.capped("engine_oil")?.let { canonicalOilViscosity(it) ?: it }, c("engine_oil")),
        frontTireSize = ai(frontTireSize.capped("front_tire_size")?.let { normalizeTireSize(it) }, c("front_tire_size")),
        rearTireSize = ai(rearTireSize.capped("rear_tire_size")?.let { normalizeTireSize(it) }, c("rear_tire_size")),
        recommendedFuel = fuel?.let { SuggestedField(it.option, c("recommended_fuel").atMost(it.confidence), FieldSource.AI) },
        cylinderCount = ai(cylinderCount?.takeIf { it in MIN_CYLINDERS..MAX_CYLINDERS }, c("cylinder_count")),
        frontBulbType = ai(frontBulbType.capped("front_bulb_type"), c("front_bulb_type")),
        rearBulbType = ai(rearBulbType.capped("rear_bulb_type"), c("rear_bulb_type")),
        batterySpecs = ai(batterySpecs.capped("battery_specs"), c("battery_specs")),
        coolantType = ai(coolantType.capped("coolant_type"), c("coolant_type")),
        frontTirePressureBelow160 = ai(frontTirePressureBelow160?.takeIf { it in MIN_TIRE_PRESSURE..MAX_TIRE_PRESSURE }, c("front_tire_pressure_below_160")),
        rearTirePressureBelow160 = ai(rearTirePressureBelow160?.takeIf { it in MIN_TIRE_PRESSURE..MAX_TIRE_PRESSURE }, c("rear_tire_pressure_below_160")),
        frontTirePressureAbove160 = ai(frontTirePressureAbove160?.takeIf { it in MIN_TIRE_PRESSURE..MAX_TIRE_PRESSURE }, c("front_tire_pressure_above_160")),
        rearTirePressureAbove160 = ai(rearTirePressureAbove160?.takeIf { it in MIN_TIRE_PRESSURE..MAX_TIRE_PRESSURE }, c("rear_tire_pressure_above_160")),
        firstRegistrationDate = ai(firstRegistrationDate?.let { parseIsoDate(it, ctx.today)?.toUtcMidnightMillis() }, c("first_registration_date")),
        nextItvDate = ai(nextItvDate?.let { parseIsoDate(it, ctx.today)?.toUtcMidnightMillis() }, c("next_itv_date")),
    )
}

private fun DExtractedMaintenance.toDraft(id: String, vehicle: VehicleRef, ctx: DraftContext): MaintenanceDraft {
    fun c(field: String) = confidenceOf(fieldsConfidence, field)
    val parsedDate = date?.let { parseIsoDate(it, ctx.today) }
    return MaintenanceDraft(
        id = id,
        description = ai(description?.trim()?.take(STRING_CAPS.getValue("description"))?.takeIf { it.isNotEmpty() }, c("description")),
        date = ai(parsedDate?.toUtcMidnightMillis(), c("date")),
        datePrecision = parsedDate?.let { DatePrecision.DAY },
        kilometers = ai(km?.takeIf { it in 0..MAX_KM }, c("km")),
        cost = ai(cost?.takeIf { it in 0.0..MAX_COST }, c("cost")),
        workshop = ai(workshop.capped("workshop"), c("workshop")),
        mechanic = ai(mechanic.capped("mechanic"), c("mechanic")),
        vehicle = vehicle,
    )
}

private fun DExtractedSparePart.toDraft(id: String, vehicle: VehicleRef, ctx: DraftContext): SparePartDraft {
    fun c(field: String) = confidenceOf(fieldsConfidence, field)
    val catalogType = sparePartId?.let { ctx.sparePartCatalog.sparePartTypes[it] }
    // A known type fixes its category; an unknown category id is dropped.
    val categoryId = catalogType?.categoryId ?: categoryId?.takeIf { it in ctx.sparePartCatalog.categories }
    return SparePartDraft(
        id = id,
        categoryId = ai(categoryId, c("category_id")),
        typeId = ai(catalogType?.id, c("spare_part_id")),
        brand = ai(brand.capped("brand"), c("brand")),
        model = ai(model.capped("model"), c("model")),
        reference = ai(reference.capped("reference"), c("reference")),
        quantity = ai(quantity?.takeIf { it > 0.0 && it <= MAX_QUANTITY }, c("quantity")),
        unit = ai(unit.capped("unit") ?: catalogType?.unit, c("unit")),
        vehicle = vehicle,
    )
}

private fun parseIsoDate(raw: String, today: LocalDate): LocalDate? {
    val date = try {
        LocalDate.parse(raw.trim())
    } catch (_: IllegalArgumentException) {
        return null
    }
    return date.takeIf { it.year in 1900..(today.year + 1) }
}
